import { useState, useCallback, useEffect } from "react";
import { Plus } from "lucide-react";
import { useOrgContext } from "../context/OrgContext";
import { useAuth } from "../context/AuthContext";
import { useLocale } from "../context/LanguageContext";
import {
  ItemCatalogGrid,
  CreateItemDrawer,
  QuickCreateTemplates,
  DeactivateConfirmDialog,
  useOrganizationMarketplaceItems,
  useCreateMarketplaceItem,
  useUpdateMarketplaceItem,
  useDeactivateMarketplaceItem,
  useImageUpload,
} from "../features/marketplace";
import type {
  MarketplaceItem,
  MarketplaceCategory,
  ItemTemplate,
} from "../features/marketplace";

export function RewardsPage() {
  const { currentOrgId, currentOrg } = useOrgContext();
  const { user } = useAuth();
  const { t } = useLocale();
  const isOrgAdmin = user?.role === "ORG_ADMIN";

  // ─── Search & Filter State ──────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] =
    useState<MarketplaceCategory | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ─── Data Fetching ──────────────────────────────────────────────────────
  const { data, loading } = useOrganizationMarketplaceItems({
    organizationId: currentOrgId ?? "",
    search: debouncedSearch || undefined,
    category: categoryFilter ?? undefined,
    limit: 100,
  });

  const items = data?.organizationMarketplaceItems?.items ?? [];
  const total = data?.organizationMarketplaceItems?.total ?? 0;

  // ─── Drawer State ──────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MarketplaceItem | undefined>();
  const [templateFill, setTemplateFill] = useState<ItemTemplate | null>(null);

  // ─── Deactivate Dialog ─────────────────────────────────────────────────
  const [deactivateTarget, setDeactivateTarget] =
    useState<MarketplaceItem | null>(null);

  // ─── Mutations ─────────────────────────────────────────────────────────
  const [createItem, { loading: creating }] = useCreateMarketplaceItem();
  const [updateItem, { loading: updating }] = useUpdateMarketplaceItem();
  const [deactivateItem, { loading: deactivating }] =
    useDeactivateMarketplaceItem();
  const { upload: uploadImage, uploading: uploadingImage } = useImageUpload({
    purpose: "MARKETPLACE",
  });

  // ─── Handlers ──────────────────────────────────────────────────────────

  const openCreateDrawer = useCallback(() => {
    setEditingItem(undefined);
    setTemplateFill(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((item: MarketplaceItem) => {
    setEditingItem(item);
    setTemplateFill(null);
    setDrawerOpen(true);
  }, []);

  const handleDuplicate = useCallback((item: MarketplaceItem) => {
    setEditingItem(undefined);
    setTemplateFill({
      name: `${item.name}${t("common.copySuffix")}`,
      description: item.description,
      shortDescription: item.shortDescription ?? "",
      koinsPrice: item.koinsPrice,
      category: item.category,
      fulfillmentType: item.fulfillmentType,
      fulfillmentInstructions: item.fulfillmentInstructions ?? "",
      emoji: "",
    });
    setDrawerOpen(true);
  }, [t]);

  const handleDeactivate = useCallback((item: MarketplaceItem) => {
    setDeactivateTarget(item);
  }, []);

  const handleTemplateSelect = useCallback((template: ItemTemplate) => {
    setEditingItem(undefined);
    setTemplateFill(template);
    setDrawerOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (input: Record<string, unknown>, imageFile?: File | null) => {
      try {
        let imageUrl: string | undefined;

        // Upload image first if provided
        if (imageFile) {
          // Backend requires a valid 24-hex-char ObjectId for the S3 key path.
          // For new items, generate a temporary ObjectId so the upload succeeds.
          const entityId =
            editingItem?._id ??
            Array.from(crypto.getRandomValues(new Uint8Array(12)))
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");
          imageUrl = await uploadImage(imageFile, entityId);
          input.imageUrl = imageUrl;
        }

        if (editingItem) {
          await updateItem({
            variables: { id: editingItem._id, input },
          });
          console.info(
            "[RewardsPage] Item updated:",
            editingItem._id,
          );
        } else {
          // Enforce org scope for ORG_ADMIN
          if (isOrgAdmin && currentOrgId) {
            input.scope = "ORGANIZATION";
            input.organizationId = currentOrgId;
          }
          await createItem({ variables: { input } });
          console.info("[RewardsPage] Item created");
        }

        setDrawerOpen(false);
        setEditingItem(undefined);
        setTemplateFill(null);
      } catch (err) {
        console.error("[RewardsPage] Submit failed:", err);
      }
    },
    [
      editingItem,
      uploadImage,
      updateItem,
      createItem,
      isOrgAdmin,
      currentOrgId,
    ],
  );

  const handleConfirmDeactivate = useCallback(async () => {
    if (!deactivateTarget) return;
    try {
      await deactivateItem({ variables: { id: deactivateTarget._id } });
      console.info(
        "[RewardsPage] Item deactivated:",
        deactivateTarget._id,
      );
      setDeactivateTarget(null);
    } catch (err) {
      console.error("[RewardsPage] Deactivate failed:", err);
    }
  }, [deactivateTarget, deactivateItem]);

  // ─── Guard: no org selected (ORG_ADMIN) ─────────────────────────────────
  if (isOrgAdmin && (!currentOrgId || !currentOrg)) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-medium text-foreground">
          {t("common.selectOrg")}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("common.selectOrgHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("rewards.title")}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {currentOrg
              ? t("rewards.descriptionOrg", { orgName: currentOrg.name })
              : t("rewards.descriptionAll")}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateDrawer}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          {t("rewards.createReward")}
        </button>
      </div>

      {/* Quick Create Templates */}
      <QuickCreateTemplates onSelect={handleTemplateSelect} />

      {/* Item Catalog Grid */}
      <ItemCatalogGrid
        items={items}
        total={total}
        isLoading={loading}
        search={search}
        categoryFilter={categoryFilter}
        onSearchChange={setSearch}
        onCategoryFilterChange={setCategoryFilter}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDeactivate={handleDeactivate}
      />

      {/* Create / Edit Drawer */}
      <CreateItemDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingItem(undefined);
          setTemplateFill(null);
        }}
        onSubmit={handleSubmit}
        loading={creating || updating || uploadingImage}
        item={editingItem}
        templateFill={templateFill}
        currentOrgId={currentOrgId}
        isOrgAdmin={isOrgAdmin}
      />

      {/* Deactivate Confirmation */}
      {deactivateTarget && (
        <DeactivateConfirmDialog
          item={deactivateTarget}
          onConfirm={handleConfirmDeactivate}
          onCancel={() => setDeactivateTarget(null)}
          loading={deactivating}
        />
      )}
    </div>
  );
}
